import { useEffect, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Typography
} from '@mui/material';
import { authGet } from '../../services/apiService';
import { hasPermission } from '../../services/authService';

/**
 * "query" link for the header of a Vital Signs card. Opens a dialog with the exact query behind the number
 * (L2 Open shows the admin-configured rule as it ran). `tile` is one of the keys the server accepts:
 * topCustomers, l2Open, sreOpen, l3Swat, l3Platform. Loads when first opened.
 * `light` is for links on a coloured header (white text). Shown to Admins only.
 */
const VitalSignsQueryLink = (props) => (hasPermission('system:admin') ? <AdminQueryLink {...props} /> : null);

const AdminQueryLink = ({ tile, light = true }) => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(-1); // index of the section just copied

  // Re-read on every open: the L2 Open rule is configurable.
  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setData(null);
    setError(null);
    setCopied(-1);
    authGet(`/api/customer-issues/vital-signs/query/${tile}`, null, 'json', { useCache: false })
      .then((res) => {
        if (cancelled) return;
        if (!res.success) throw new Error(res.message || 'Could not load the query.');
        setData(res);
        setError(null);
      })
      .catch((err) => { if (!cancelled) setError((err && err.message) || 'Could not load the query.'); });
    return () => { cancelled = true; };
  }, [open, tile]);

  const copy = async (body, index) => {
    try {
      await navigator.clipboard.writeText(body);
      setCopied(index);
    } catch (_err) { /* clipboard unavailable: the text stays selectable */ }
  };

  return (
    <>
      <Button
        size="small"
        onClick={() => setOpen(true)}
        sx={{
          color: light ? '#fff' : 'primary.main', textTransform: 'none', textDecoration: 'underline', minWidth: 0, py: 0,
          '&:hover': { textDecoration: 'underline', bgcolor: light ? 'rgba(255,255,255,0.15)' : undefined }
        }}
      >
        query
      </Button>
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{data ? `${data.title} query` : 'Query'}</DialogTitle>
        <DialogContent dividers>
          {error && <Alert severity="error">{error}</Alert>}
          {!data && !error && <CircularProgress size={24} />}
          {data && (
            <>
              <Typography variant="body2" sx={{ mb: 2 }}>{data.summary}</Typography>
              {(data.sections || []).map((part, index) => (
                <Box key={part.heading} sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: 0.5 }}>{part.heading}</Typography>
                    <Button size="small" onClick={() => copy(part.body, index)} sx={{ textTransform: 'none', py: 0 }}>
                      {copied === index ? 'Copied' : 'Copy'}
                    </Button>
                  </Box>
                  <Box
                    component="pre"
                    sx={{ m: 0, mt: 0.5, p: 1.5, bgcolor: '#f5f5f5', borderRadius: 1, fontSize: '0.75rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                  >
                    {part.body}
                  </Box>
                </Box>
              ))}
            </>
          )}
        </DialogContent>
        <DialogActions><Button onClick={() => setOpen(false)}>Close</Button></DialogActions>
      </Dialog>
    </>
  );
};

export default VitalSignsQueryLink;
